import React from "react";
import { Box, Button, Icon, Text, type ButtonProps } from "@salestrak/ui";
import { formatAge } from "#app/lib/issue-utils";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import type { Omittable } from "@salestrak/ui";

export type AgeLabelProps = Omittable<{
  age?: { date?: string | null; status?: string | null } | null;
  size?: "xs" | "sm";
  buttonSize?: ButtonProps["size"];
  buttonVariant?: ButtonProps["variant"];
  onClick?: () => void;
}>;

export const AgeLabel = React.memo(
  function AgeLabel({
    age,
    size = "sm",
    buttonSize = "xs",
    buttonVariant = "ghost",
    onClick,
  }: AgeLabelProps) {
    const status = age?.status;
    const isDisabled = !status || status === "disabled";
    // Icon scales with the text size so smaller labels get a smaller clock.
    const iconSize = size === "xs" ? "sm" : "md";

    if (isDisabled) {
      const icon = <Icon name="clock" size={iconSize} color="muted" />;
      if (onClick) {
        return (
          <Button variant={buttonVariant} size={buttonSize} onClick={onClick}>
            {icon}
          </Button>
        );
      }
      return icon;
    }

    const formatted = formatAge(age?.date);
    if (!formatted) return null;

    // `decoColor` is the age status' palette hue — the SAME hue the age chart
    // slices paint (`use-dimension-counts.ts`), and one `color=` name keeps
    // the clock glyph and the label on one value. The semantic pair
    // (`--destructive` glyph / `--destructive-foreground` text) splits them.
    const config =
      AGE_STATUS_CONFIG[status as AgeStatus] ?? AGE_STATUS_CONFIG.offTrack;
    const tone = config.decoColor === "grey" ? "muted" : config.decoColor;

    const content = (
      <Box direction="row" gap="xs" align="center">
        <Icon name="clock" size={iconSize} color={tone} />
        <Text size={size} color={tone}>
          {formatted}
        </Text>
      </Box>
    );

    if (onClick) {
      return (
        <Button variant={buttonVariant} size={buttonSize} onClick={onClick}>
          {content}
        </Button>
      );
    }

    return content;
  },
  (prev, next) =>
    prev.size === next.size &&
    prev.buttonSize === next.buttonSize &&
    prev.buttonVariant === next.buttonVariant &&
    prev.age?.date === next.age?.date &&
    prev.age?.status === next.age?.status &&
    prev.onClick === next.onClick,
);
